import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { ArrowRight } from "@phosphor-icons/react";
import { LogoOnly } from "@/components/Brand";
import { INDUSTRIES } from "@/lib/industries";

export default function Signup() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [industry, setIndustry] = useState("general");
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const q = params.get("industry");
    if (q && INDUSTRIES.some((i) => i.slug === q)) setIndustry(q);
  }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (!consent) {
      toast.error("Please accept the Terms and Privacy Notice to continue");
      return;
    }
    setSubmitting(true);
    try {
      await register(email, password, fullName, industry);
      toast.success("Workspace created");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Signup failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="flex items-center justify-center p-8 bg-zinc-100 order-2 lg:order-1">
        <form onSubmit={submit} className="w-full max-w-md bg-white border border-zinc-200 p-10" data-testid="signup-form">
          <div className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-500">Sign up</div>
          <h2 className="font-display font-black tracking-tighter text-3xl mt-2">Create workspace</h2>

          <label className="block mt-8 text-xs tracking-[0.2em] uppercase font-bold text-zinc-600">Full name</label>
          <input
            type="text"
            required
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            className="w-full mt-2 h-12 px-4 border border-zinc-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950 focus:border-zinc-950"
            data-testid="signup-name-input"
            placeholder="Jane Doe"
          />

          <label className="block mt-5 text-xs tracking-[0.2em] uppercase font-bold text-zinc-600">Email</label>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full mt-2 h-12 px-4 border border-zinc-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950 focus:border-zinc-950"
            data-testid="signup-email-input"
            placeholder="you@company.com"
          />

          <label className="block mt-5 text-xs tracking-[0.2em] uppercase font-bold text-zinc-600">Password</label>
          <input
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full mt-2 h-12 px-4 border border-zinc-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950 focus:border-zinc-950"
            data-testid="signup-password-input"
            placeholder="At least 6 characters"
          />

          <label className="block mt-5 text-xs tracking-[0.2em] uppercase font-bold text-zinc-600">Industry</label>
          <select
            value={industry}
            onChange={(e) => setIndustry(e.target.value)}
            className="w-full mt-2 h-12 px-4 border border-zinc-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950 focus:border-zinc-950"
            data-testid="signup-industry-select"
          >
            {INDUSTRIES.map((i) => (
              <option key={i.slug} value={i.slug}>{i.label}</option>
            ))}
          </select>
          <p className="mt-1.5 text-[11px] text-zinc-500">Tailors the AI lenses to your domain. You can change this later.</p>

          <label className="mt-6 flex items-start gap-2.5 text-xs text-zinc-700 leading-relaxed cursor-pointer select-none">
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-[#0020F5] shrink-0"
              data-testid="signup-consent-checkbox"
            />
            <span>
              I agree to the <Link to="/terms" target="_blank" className="font-semibold text-zinc-950 underline" data-testid="signup-terms-link">Terms of Service</Link> and <Link to="/privacy" target="_blank" className="font-semibold text-zinc-950 underline" data-testid="signup-privacy-link">Privacy Notice</Link>.
            </span>
          </label>

          <button
            type="submit"
            disabled={submitting}
            className="mt-6 w-full h-12 bg-zinc-950 text-white text-sm font-semibold hover:bg-zinc-800 transition-colors disabled:opacity-50 inline-flex items-center justify-center"
            data-testid="signup-submit-button"
          >
            {submitting ? "Creating..." : <>Create workspace <ArrowRight size={16} weight="bold" className="ml-2" /></>}
          </button>

          <div className="text-sm text-zinc-600 mt-6">
            Have an account?{" "}
            <Link to="/login" className="font-semibold text-zinc-950 hover:underline" data-testid="signup-link-login">
              Sign in
            </Link>
          </div>
        </form>
      </div>

      <div className="hidden lg:flex flex-col justify-between bg-[#0020F5] text-white p-12 relative overflow-hidden order-1 lg:order-2">
        <div className="absolute inset-0 bg-grid-dense opacity-20" />
        <Link to="/" className="relative flex items-center gap-3" data-testid="signup-brand">
          <div className="bg-white p-1">
            <LogoOnly size={28} />
          </div>
          <div className="flex flex-col leading-tight">
            <span className="font-display font-black tracking-tight text-lg">ARISSAI</span>
            <span className="text-[10px] tracking-[0.18em] uppercase font-semibold text-white/70">Project Intelligence</span>
          </div>
        </Link>
        <div className="relative">
          <div className="text-xs tracking-[0.2em] uppercase font-bold text-white/60">// Join the panel</div>
          <h1 className="font-display font-black tracking-tighter text-5xl mt-4">
            Decisions, <br />defended.
          </h1>
          <p className="text-white/80 mt-4 max-w-md">
            Bring your project. ARISSAI brings the panel of AI experts. No credit card. No setup.
          </p>
        </div>
        <div className="relative text-[11px] tracking-[0.2em] uppercase font-bold text-white/60 font-mono">
          © 2026 ARISSAI
        </div>
      </div>
    </div>
  );
}
